/**
 * Th + Td — shared table-cell helpers used by every paginated list
 * screen in the dashboard.
 *
 * Replaces the local `function Th` (19 redeclarations) and `function Td`
 * (18 redeclarations) that were scattered across pages (purged
 * 2026-05-19). Single source for cell padding, alignment, and the
 * thead-row text color.
 *
 * Pattern: pages keep their own `<table>` shell + `<thead>`/`<tbody>`
 * because the row shape is page-specific, but use these helpers
 * for the cells. Mirrors the Field molecule's role for form fields.
 *
 * Per SOP 13: `px-4 py-3` padding, `text-left font-medium` headers,
 * `align-middle` data cells. `text-text-secondary` is applied by the
 * parent `<thead>` via the `bg-parchment/50 text-text-secondary`
 * className that pages set on their header row.
 */

import type { ReactNode } from 'react'

interface ThProps {
  readonly children: ReactNode
  readonly className?: string
}

interface TdProps {
  readonly children: ReactNode
  readonly className?: string
  readonly title?: string
}

export function Th({ children, className = '' }: ThProps): JSX.Element {
  return <th className={`text-left font-medium px-4 py-3 ${className}`}>{children}</th>
}

export function Td({ children, className = '', title = '' }: TdProps): JSX.Element {
  return (
    <td className={`px-4 py-3 align-middle ${className}`} title={title}>
      {children}
    </td>
  )
}
